import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
  cn,
  Label,
} from "@atlas/core";
import { formatIsoDateTimeDisplay, useTranslation } from "@atlas/sdk";
import type { IHubCatalogVisibility } from "@enums";
import type { IHubPackage } from "@models";
import { getHubCatalogVisibilityBadge, plainCatalogDescription } from "@utils";
import type { ReactNode } from "react";

export interface PackageCardProps {
  pkg: IHubPackage;
  catalogCount: number;
  isActive?: boolean;
  isExportSelected?: boolean;
  onClick?: () => void;
  actions?: ReactNode;
}

export function PackageCard({
  pkg,
  catalogCount,
  isActive = false,
  isExportSelected = false,
  onClick,
  actions,
}: PackageCardProps) {
  const { t } = useTranslation();
  return (
    <Card
      className={cn(
        "h-65 w-85",
        onClick && "cursor-pointer",
        isActive &&
          !isExportSelected &&
          "bg-intelligence/50 border-intelligence",
        !isActive && isExportSelected && "border-primary",
        isActive && isExportSelected && "bg-intelligence/50 border-primary",
      )}
      onClick={onClick}>
      <CardContent className="flex flex-col gap-4 p-4 min-h-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col items-start gap-2">
            {pkg.visibility &&
              getHubCatalogVisibilityBadge(
                pkg.visibility as unknown as IHubCatalogVisibility,
                t,
                { className: "font-normal" },
              )}
            <CardTitle className="line-clamp-2 leading-tight">
              {pkg.label || pkg.name}
            </CardTitle>
          </div>
        </div>

        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-3 self-stretch">
          <Label className="text-xs text-muted-foreground line-clamp-3">
            {plainCatalogDescription(pkg.description ?? "")}
          </Label>
          {pkg.createdByDatetime && (
            <Label className="text-xs text-muted-foreground mt-auto">
              {t("hub.checkout.summary_created", {
                date: formatIsoDateTimeDisplay(pkg.createdByDatetime),
                defaultValue: "Created on {{date}}",
              })}
            </Label>
          )}
        </div>
      </CardContent>

      <CardFooter className="justify-between">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t("hub.sidebar.catalogs", {
              defaultValue: "Catalogs",
            })}
            : {catalogCount}
          </Label>
        </div>
        {actions && <div className="flex items-center gap-1">{actions}</div>}
      </CardFooter>
    </Card>
  );
}
